'use client';

import Image from 'next/image';
import { Carousel, Container } from 'react-bootstrap';
import type { PortableTextBlock } from 'next-sanity';

import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import { urlForImage } from '@/sanity/lib/utils';
import type { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';
import { getSectionBackgroundStyle } from '../../utils';
import './HowItWorksSection.scss';

type LandingPageData = Extract<
  NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>,
  { _type: 'landingPage' }
>;

interface HowItWorksSectionProps {
  data: NonNullable<LandingPageData['howItWorks']>;
}

export default function HowItWorksSection({ data }: HowItWorksSectionProps) {
  const { sectionLabel, headline, steps, supportingImages, background } = data;

  return (
    <section
      className="rlp-how-it-works"
      style={getSectionBackgroundStyle(background)}
    >
      <Container>
        {sectionLabel && (
          <span className="rlp-section-label">{sectionLabel}</span>
        )}
        <div className="rlp-how-it-works-content">
          {headline && (
            <div className="rlp-section-headline">
              <LandingPortableText value={headline as PortableTextBlock[]} />
            </div>
          )}

          {supportingImages && supportingImages.length > 0 && (
            <div className="rlp-how-it-works-images">
              <Carousel
                className="rlp-carousel"
                indicators={supportingImages.length > 1}
                controls={false}
                interval={4000}
                pause="hover"
              >
                {supportingImages.map((item) => (
                  <Carousel.Item key={item._key}>
                    <div className="rlp-how-it-works-image">
                      <Image
                        src={
                          item.image
                            ? (urlForImage(item.image)
                                ?.width(950)
                                .height(950)
                                .fit('crop')
                                .url() as string)
                            : ''
                        }
                        alt={item.alt || ''}
                        width={475}
                        height={475}
                        sizes="475px"
                      />
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel>
            </div>
          )}

          {steps && steps.length > 0 && (
            <ol className="rlp-how-it-works-steps">
              {steps.map((step) => (
                <li key={step._key}>
                  <span className="rlp-how-it-works-step-number">
                    {step.title && <h3>{step.title}</h3>}
                  </span>

                  {step.description && (
                    <LandingPortableText
                      value={step.description as PortableTextBlock[]}
                    />
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </Container>
    </section>
  );
}
